import type { ComponentProps, FC, FormEvent, ReactElement } from "react";
import { cloneElement, isValidElement, useId } from "react";
import { Form } from "react-router";
import { toClassList } from "~/utils/classes";
import ButtonComponent from "../Button";
import InputComponent from "../Input";
import "./style.css";

type InputElement = ReactElement<
  ComponentProps<typeof InputComponent>,
  typeof InputComponent
>;
type ButtonElement = ReactElement<
  ComponentProps<typeof ButtonComponent>,
  typeof ButtonComponent
>;

type Props = {
  Input: InputElement;
  Button: ButtonElement;
  intent?: string;
  disabled?: boolean;
  className?: string;
  onSubmit?: (e: FormEvent<HTMLFormElement>) => void;
};

function assertInputSlot(element: InputElement) {
  if (!isValidElement(element) || element.type !== InputComponent) {
    throw new Error(
      "InputForm's `Input` prop must be an <Input /> element from ~/components/lib/Input.",
    );
  }

  if (!element.props.name) {
    throw new Error(
      "InputForm's `Input` element must have a `name` prop so its value is included in the submitted form data.",
    );
  }
}

function assertButtonSlot(element: ButtonElement) {
  if (!isValidElement(element) || element.type !== ButtonComponent) {
    throw new Error(
      "InputForm's `Button` prop must be a <Button /> element from ~/components/lib/Button.",
    );
  }

  if (element.props.href || element.props.to) {
    throw new Error(
      "InputForm's `Button` can't render as a link (`href`/`to`) — it must submit the form.",
    );
  }
}

const InputForm: FC<Props> = ({
  Input,
  Button,
  intent,
  disabled = false,
  className = "",
  onSubmit,
}) => {
  assertInputSlot(Input);
  assertButtonSlot(Button);

  const generatedId = useId();
  const inputId = Input.props.id ?? generatedId;
  const hasError = !!Input.props.error;

  const formClasses = toClassList([
    "input-form",
    hasError ? "input-form--error" : "",
    disabled ? "input-form--disabled" : "",
    className,
  ]);

  return (
    <Form
      className={formClasses}
      onSubmit={onSubmit}
      method="post"
    >
      {Input.props.label && (
        <label
          className="input-form__label"
          htmlFor={inputId}
        >
          {Input.props.label}
        </label>
      )}
      <div className="input-form__row">
        {cloneElement(Input, {
          id: inputId,
          label: undefined,
          className: toClassList([
            "input-form__input",
            Input.props.className ?? "",
          ]),
          disabled: disabled || Input.props.disabled,
        })}
        {intent && (
          <input
            type="text"
            hidden
            name="intent"
            value={intent}
            readOnly
          />
        )}
        {cloneElement(Button, {
          type: "submit",
          className: toClassList([
            "input-form__button",
            Button.props.className ?? "",
          ]),
          disabled: disabled || Button.props.disabled,
        })}
      </div>
    </Form>
  );
};

export default InputForm;
